import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Settings, Package, Plus, Trash2 } from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';

interface BillingSettingsModalProps {
    open: boolean;
    onClose: () => void;
    onSaved?: () => void | Promise<void>;
    /** Rendu "en place" (sans fond noir ni cadre flottant) pour être intégré comme un onglet dans une
     *  page — utilisé par la page Paramètres centrale, en plus de l'usage en modale classique. */
    embedded?: boolean;
}

interface BillingSettings {
    company_name: string;
    logo_url: string;
    default_tva_rate?: string;
    default_tva_custom_rate?: number | null;
    can_edit_branding?: boolean;
    can_edit_tax_rate?: boolean;
    tva_rates?: number[];
    address: string;
    postal_code: string;
    city: string;
    country: string;
    siret: string;
    tva: string;
    phone: string;
    capital: string;
    ape: string;
    siege_social: string;
    rcs_ville: string;
    rcs_numero: string;
    prestations_catalog: string;
    payment_delay_days?: number;
    // Facturation électronique (phase 1) : coordonnées de paiement et régime de TVA du vendeur,
    // exigés par la norme EN 16931 et repris tels quels sur la facture.
    iban?: string;
    bic?: string;
    tva_regime?: 'normal' | 'franchise_293b';
    tva_on_debits?: boolean | number;
    // Plus de clé secrète ici : elle ne descend plus jamais du serveur (durcissement 2026-09-06),
    // et se configure dans Paramètres > Application.
    stripe_configured?: boolean;
}

interface CatalogItem {
    id: string;
    label: string;
    unit_price_ttc?: number;
    tax_rate?: number;
    // Objet d'origine tel qu'il est stocké : cet écran ne gère que le libellé, le prix et la TVA, mais le
    // catalogue porte bien plus (temps de travail par unité, catégorie, portée Pro/Particulier, type de
    // tarif, dimensions). Sans le conserver ici, enregistrer depuis ce modal réécrivait le catalogue avec
    // ces seuls champs et effaçait tout le reste (2026-09-06, Emeni : « pourtant côté client pro les
    // prestations ont été enregistrées avec leurs temps de travail »).
    raw?: Record<string, any>;
}

const EMPTY: BillingSettings = {
    company_name: '', logo_url: '',
    address: '', postal_code: '', city: '', siret: '', tva: '',
    country: 'France', phone: '', capital: '', ape: '', siege_social: '', rcs_ville: '', rcs_numero: '', prestations_catalog: '[]',
    payment_delay_days: 30,
    iban: '', bic: '', tva_regime: 'normal', tva_on_debits: false,
};

const Field = ({
    label,
    value,
    onChange,
    className = '',
    readOnly = false,
}: {
    label: string;
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    className?: string;
    readOnly?: boolean;
}) => (
    <div className={`flex flex-col gap-1 ${className}`}>
        <label className="text-[10px] font-bold uppercase tracking-widest text-[var(--text-muted)]">{label}</label>
        <input
            type="text"
            value={value}
            onChange={onChange}
            readOnly={readOnly}
            className={`h-8 px-2 text-sm bg-transparent border border-[var(--border-color)] rounded-lg text-[var(--text-primary)] transition-colors ${readOnly ? 'opacity-70 cursor-not-allowed' : 'focus:outline-none focus:border-brand-500'}`}
        />
    </div>
);

export function BillingSettingsModal({ open, onClose, onSaved, embedded = false }: BillingSettingsModalProps) {
    const { user } = useAuth();
    // Pour les clients "Prestation", le catalogue est géré par le module dédié "Prestations"
    // (avec des champs supplémentaires : durée, type d'unité) — un seul éditeur pour cette donnée.
    const showPrestationsTab = user?.client_type !== 'prestation';
    const [tab, setTab] = React.useState<'general' | 'prestations'>('general');
    const [form, setForm] = React.useState<BillingSettings>(EMPTY);
    const [saving, setSaving] = React.useState(false);
    const [saved, setSaved] = React.useState(false);
    // Une erreur de validation renvoyée par le serveur (IBAN dont la clé ne tombe pas juste, BIC mal
    // formé) était jusqu'ici avalée par un catch vide : l'écran affichait « enregistré » alors que rien
    // ne l'était. Elle est maintenant montrée telle quelle.
    const [saveError, setSaveError] = React.useState('');
    const [catalogItems, setCatalogItems] = React.useState<CatalogItem[]>([]);
    const [newCatalogItem, setNewCatalogItem] = React.useState('');

    React.useEffect(() => {
        if (open) {
            moduleApi.getBillingSettings().then((data: any) => {
                if (data) {
                    const merged = { ...EMPTY, ...data };
                    setForm(merged);
                    try {
                        const parsed = JSON.parse(merged.prestations_catalog || '[]');
                        if (Array.isArray(parsed)) {
                            setCatalogItems(
                                parsed
                                    .map((p: any, index: number) => ({
                                        id: String(p?.id || index + 1),
                                        label: String(p?.label || '').trim(),
                                        unit_price_ttc: Number(p?.unit_price_ttc ?? 0),
                                        tax_rate: Number(p?.tax_rate ?? 0),
                                        raw: (p && typeof p === 'object') ? p : {},
                                    }))
                                    .filter((item: CatalogItem) => item.label.length > 0)
                            );
                        } else {
                            setCatalogItems([]);
                        }
                    } catch {
                        setCatalogItems([]);
                    }
                    setNewCatalogItem('');
                }
            }).catch(() => {});
        }
    }, [open]);

    const set = (field: keyof BillingSettings) => (e: React.ChangeEvent<HTMLInputElement>) =>
        setForm(f => ({ ...f, [field]: e.target.value }));

    const allowedTvaRates = (form.tva_rates && form.tva_rates.length > 0 ? form.tva_rates : [20]).map(Number).filter((n) => Number.isFinite(n) && n >= 0);

    const addCatalogItem = () => {
        const label = newCatalogItem.trim();
        if (!label) return;
        setCatalogItems((prev) => [...prev, { id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, label, unit_price_ttc: 0, tax_rate: allowedTvaRates[0] ?? 20 }]);
        setNewCatalogItem('');
    };

    const updateCatalogItem = (id: string, value: string) => {
        setCatalogItems((prev) => prev.map((item) => item.id === id ? { ...item, label: value } : item));
    };

    const updateCatalogPrice = (id: string, value: string) => {
        const numericValue = Math.max(0, Number(value || 0));
        setCatalogItems((prev) => prev.map((item) => item.id === id ? { ...item, unit_price_ttc: numericValue } : item));
    };

    const updateCatalogTaxRate = (id: string, value: string) => {
        const numericValue = Math.max(0, Number(value || 0));
        setCatalogItems((prev) => prev.map((item) => item.id === id ? { ...item, tax_rate: numericValue } : item));
    };

    const removeCatalogItem = (id: string) => {
        setCatalogItems((prev) => prev.filter((item) => item.id !== id));
    };

    const handleSave = async () => {
        setSaving(true);
        setSaveError('');
        try {
            // On repart de l'objet stocké et on n'écrase QUE ce que cet écran édite. L'identifiant n'est
            // jamais renuméroté : les formules et les lignes de devis désignent une prestation par cet
            // identifiant, le réindexer sur la position les ferait silencieusement pointer une autre
            // prestation.
            const prestations_catalog = JSON.stringify(
                catalogItems
                    .filter((item) => item.label.trim().length > 0)
                    .map((item) => ({
                        ...(item.raw || {}),
                        id: item.id,
                        label: item.label.trim(),
                        unit_price_ttc: Number(item.unit_price_ttc ?? 0),
                        tax_rate: Number(item.tax_rate ?? allowedTvaRates[0] ?? 20),
                    }))
            );
            const { logo_url, default_tva_rate, default_tva_custom_rate, can_edit_branding, can_edit_tax_rate, ...safeForm } = form as any;
            await moduleApi.saveBillingSettings({ ...safeForm, prestations_catalog });
            await onSaved?.();
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
        } catch (e: any) {
            setSaveError(e?.message || "L'enregistrement a échoué.");
        }
        finally { setSaving(false); }
    };

    // Contenu des 2 onglets, partagé entre le rendu "modale" et le rendu "onglet intégré" ci-dessous.
    const generalTabContent = (
        <div className="flex flex-col gap-3">
            <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--text-muted)] mb-1">Informations légales</p>

            {/* Ligne 1 : Adresse pleine largeur */}
            <Field label="Rue / Adresse postale" value={form.address} onChange={set('address')} />

            {/* Ligne 2 : Code postal | Ville */}
            <div className="grid grid-cols-2 gap-3">
                <Field label="Code Postal" value={form.postal_code} onChange={set('postal_code')} />
                <Field label="Ville" value={form.city} onChange={set('city')} />
            </div>

            <Field label="Pays" value={form.country} onChange={set('country')} />

            <div className="flex flex-col gap-1">
                <label className="text-[10px] font-bold uppercase tracking-widest text-[var(--text-muted)]">Délai de paiement par défaut (jours)</label>
                <input
                    type="number"
                    min={0}
                    step="1"
                    value={form.payment_delay_days ?? 30}
                    onChange={(e) => setForm((f) => ({ ...f, payment_delay_days: Math.max(0, Number(e.target.value || 0)) }))}
                    className="h-8 px-2 text-sm bg-transparent border border-[var(--border-color)] rounded-lg text-[var(--text-primary)] outline-none focus:border-brand-500 w-32"
                />
                <p className="text-[10px] text-[var(--text-muted)]">Utilisé pour indiquer la date d'échéance sur les factures non réglées sur place.</p>
            </div>

            {/* Ligne 3 : SIRET | TVA */}
            <div className="grid grid-cols-2 gap-3">
                <Field label="SIRET" value={form.siret} onChange={set('siret')} />
                <Field label="TVA Intracommunautaire" value={form.tva} onChange={set('tva')} />
            </div>

            {/* Ligne 4 : Téléphone | Capital Social */}
            <div className="grid grid-cols-2 gap-3">
                <Field label="Téléphone" value={form.phone} onChange={set('phone')} />
                <Field label="Capital Social" value={form.capital} onChange={set('capital')} />
            </div>

            {/* Ligne 5 : Code APE / NAF | Siège Social */}
            <div className="grid grid-cols-2 gap-3">
                <Field label="Code APE / NAF" value={form.ape} onChange={set('ape')} />
                <Field label="Siège Social (si différent)" value={form.siege_social} onChange={set('siege_social')} />
            </div>

            {/* Ligne 6 : RCS Ville | RCS Numéro */}
            <div className="grid grid-cols-2 gap-3">
                <Field label="Ville d'immatriculation RCS" value={form.rcs_ville} onChange={set('rcs_ville')} />
                <Field label="Numéro RCS" value={form.rcs_numero} onChange={set('rcs_numero')} />
            </div>

            {/* Coordonnées de paiement et régime de TVA — exigés par la facture électronique (norme
                EN 16931), et de toute façon obligatoires sur une facture papier depuis toujours. */}
            <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--text-muted)] mt-4 mb-1">Paiement et TVA</p>

            <div className="grid grid-cols-2 gap-3">
                <Field label="IBAN" value={form.iban || ''} onChange={set('iban')} />
                <Field label="BIC / SWIFT" value={form.bic || ''} onChange={set('bic')} />
            </div>
            <p className="text-[10px] text-[var(--text-muted)] -mt-1">Recopiés depuis votre relevé d'identité bancaire. L'IBAN est vérifié à l'enregistrement : une coquille est refusée plutôt qu'imprimée sur vos factures.</p>

            <div className="flex flex-col gap-1">
                <label className="text-[10px] font-bold uppercase tracking-widest text-[var(--text-muted)]">Régime de TVA</label>
                <select
                    value={form.tva_regime || 'normal'}
                    onChange={(e) => setForm((f) => ({ ...f, tva_regime: e.target.value === 'franchise_293b' ? 'franchise_293b' : 'normal' }))}
                    className="h-8 px-2 text-sm bg-transparent border border-[var(--border-color)] rounded-lg text-[var(--text-primary)] outline-none focus:border-brand-500"
                >
                    <option value="normal">Je facture la TVA (régime normal)</option>
                    <option value="franchise_293b">Franchise en base — je ne facture pas de TVA (art. 293 B)</option>
                </select>
                <p className="text-[10px] text-[var(--text-muted)]">
                    {(form.tva_regime || 'normal') === 'franchise_293b'
                        ? "Vos factures seront établies sans TVA et porteront automatiquement la mention obligatoire « TVA non applicable, art. 293 B du CGI »."
                        : "Cas le plus courant. Choisissez la franchise en base uniquement si votre chiffre d'affaires vous en fait bénéficier et que vous n'avez pas opté pour la TVA."}
                </p>
            </div>

            {(form.tva_regime || 'normal') === 'normal' && (
                <label className="flex items-start gap-2 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={!!form.tva_on_debits}
                        onChange={(e) => setForm((f) => ({ ...f, tva_on_debits: e.target.checked }))}
                        className="accent-brand-600 w-4 h-4 mt-0.5"
                    />
                    <span className="text-[11px] text-[var(--text-primary)]">
                        J'ai opté pour la <strong>TVA sur les débits</strong>
                        <span className="block text-[10px] text-[var(--text-muted)]">À cocher uniquement si vous avez fait cette demande à votre service des impôts. Sans cette option, la TVA d'une prestation est due à l'encaissement ; avec elle, dès la facturation. La mention correspondante est alors ajoutée à vos factures.</span>
                    </span>
                </label>
            )}
        </div>
    );

    const prestationsTabContent = (
        <div className="space-y-4">
            <div className="flex items-center gap-2 text-[var(--text-primary)]">
                <Package size={16} />
                <p className="text-sm font-bold">Gestion du catalogue</p>
            </div>
            <p className="text-xs text-[var(--text-muted)]">Ajoute, modifie ou supprime les prestations proposées dans la facture.</p>

            <div className="flex gap-2">
                <input
                    type="text"
                    value={newCatalogItem}
                    onChange={(e) => setNewCatalogItem(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            addCatalogItem();
                        }
                    }}
                    placeholder="Ex: Cocktail entreprise"
                    className="flex-1 h-8 px-3 text-sm bg-transparent border border-[var(--border-color)] rounded-lg text-[var(--text-primary)] focus:outline-none focus:border-brand-500 transition-colors"
                />
                <button
                    type="button"
                    onClick={addCatalogItem}
                    className="inline-flex items-center gap-1 px-3 h-8 rounded-lg bg-brand-600 hover:bg-brand-700 text-content text-xs font-bold transition-colors"
                >
                    <Plus size={13} /> Ajouter
                </button>
            </div>

            <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
                {catalogItems.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-[var(--border-color)] px-3 py-4 text-xs text-[var(--text-muted)] text-center">
                        Aucune prestation enregistrée.
                    </div>
                ) : catalogItems.map((item, index) => (
                    <div key={item.id} className="rounded-lg border border-[var(--border-color)] px-3 py-3 space-y-2">
                        <div className="flex items-center gap-2">
                            <span className="text-[10px] font-bold text-[var(--text-muted)] w-5">{index + 1}</span>
                            <input
                                type="text"
                                value={item.label}
                                onChange={(e) => updateCatalogItem(item.id, e.target.value)}
                                className="flex-1 h-8 px-2 text-sm bg-transparent border border-[var(--border-color)] rounded-lg text-[var(--text-primary)] focus:outline-none focus:border-brand-500 transition-colors"
                                placeholder="Nom de la prestation"
                            />
                            <button
                                type="button"
                                onClick={() => removeCatalogItem(item.id)}
                                className="inline-flex items-center justify-center h-8 w-8 rounded-lg border border-red-500/30 text-red-500 hover:bg-red-500/10 transition-colors"
                                aria-label="Supprimer la prestation"
                            >
                                <Trash2 size={14} />
                            </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            <div className="flex flex-col gap-1">
                                <label className="text-[10px] font-bold uppercase tracking-widest text-[var(--text-muted)]">Montant TTC à l’unité optionnel</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min={0}
                                    value={item.unit_price_ttc ?? 0}
                                    onChange={(e) => updateCatalogPrice(item.id, e.target.value)}
                                    className="h-8 px-2 text-sm bg-transparent border border-[var(--border-color)] rounded-lg text-[var(--text-primary)] focus:outline-none focus:border-brand-500 transition-colors"
                                    placeholder="Ex: 25"
                                />
                            </div>
                            <div className="flex flex-col gap-1">
                                <label className="text-[10px] font-bold uppercase tracking-widest text-[var(--text-muted)]">TVA appliquée</label>
                                <select
                                    value={item.tax_rate ?? allowedTvaRates[0] ?? 20}
                                    onChange={(e) => updateCatalogTaxRate(item.id, e.target.value)}
                                    className="h-8 px-2 text-sm bg-transparent border border-[var(--border-color)] rounded-lg text-[var(--text-primary)] focus:outline-none focus:border-brand-500 transition-colors"
                                >
                                    {allowedTvaRates.map((rate) => (
                                        <option key={rate} value={rate}>{rate}%</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    if (embedded) {
        if (!open) return null;
        return (
            <div className="w-full flex flex-col">
                {/* Tabs */}
                <div className="flex border-b border-[var(--border-color)] shrink-0">
                    <button
                        onClick={() => setTab('general')}
                        className={`flex items-center gap-2 py-3 px-1 mr-6 text-xs font-bold border-b-2 transition-colors ${
                            tab === 'general'
                                ? 'border-brand-500 text-brand-500'
                                : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-primary)]'
                        }`}
                    >
                        <Settings size={13} />
                        Général
                    </button>
                    {showPrestationsTab && (
                        <button
                            onClick={() => setTab('prestations')}
                            className={`flex items-center gap-2 py-3 px-1 text-xs font-bold border-b-2 transition-colors ${
                                tab === 'prestations'
                                    ? 'border-brand-500 text-brand-500'
                                    : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-primary)]'
                            }`}
                        >
                            <Package size={13} />
                            Prestations
                        </button>
                    )}
                </div>

                <div className="py-5">
                    {tab === 'general' && generalTabContent}
                    {tab === 'prestations' && prestationsTabContent}
                </div>

                <div className="flex items-center justify-end gap-3 pt-4 border-t border-[var(--border-color)] shrink-0">
                    {saveError && <p className="flex-1 text-[11px] font-semibold text-red-500">{saveError}</p>}
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="px-4 py-1.5 text-xs font-bold rounded-lg bg-brand-600 hover:bg-brand-700 text-content transition-colors disabled:opacity-50"
                    >
                        {saving ? 'Enregistrement...' : saved ? '✓ Enregistré' : 'Enregistrer'}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <AnimatePresence>
            {open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                        onClick={onClose}
                    />
                    <motion.div
                        initial={{ opacity: 0, scale: 0.96, y: 10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.96, y: 10 }}
                        transition={{ duration: 0.2 }}
                        className="relative w-full max-w-3xl max-h-[90vh] bg-white dark:bg-[#0A0A0A] border border-[var(--border-color)] rounded-2xl shadow-2xl z-10 flex flex-col"
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between px-4 md:px-6 py-4 border-b border-[var(--border-color)] shrink-0">
                            <h2 className="text-base font-bold text-[var(--text-primary)]">Paramètres de facturation</h2>
                            <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">
                                <X size={18} />
                            </button>
                        </div>

                        {/* Tabs */}
                        <div className="flex border-b border-[var(--border-color)] px-4 md:px-6 shrink-0">
                            <button
                                onClick={() => setTab('general')}
                                className={`flex items-center gap-2 py-3 px-1 mr-6 text-xs font-bold border-b-2 transition-colors ${
                                    tab === 'general'
                                        ? 'border-brand-500 text-brand-500'
                                        : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-primary)]'
                                }`}
                            >
                                <Settings size={13} />
                                Général
                            </button>
                            {showPrestationsTab && (
                                <button
                                    onClick={() => setTab('prestations')}
                                    className={`flex items-center gap-2 py-3 px-1 text-xs font-bold border-b-2 transition-colors ${
                                        tab === 'prestations'
                                            ? 'border-brand-500 text-brand-500'
                                            : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-primary)]'
                                    }`}
                                >
                                    <Package size={13} />
                                    Prestations
                                </button>
                            )}
                        </div>

                        {/* Body */}
                        <div className="px-4 md:px-6 py-5 overflow-y-auto">
                            {tab === 'general' && generalTabContent}
                            {tab === 'prestations' && prestationsTabContent}
                        </div>

                        {/* Footer */}
                        <div className="flex items-center justify-end gap-3 px-4 md:px-6 py-4 border-t border-[var(--border-color)] shrink-0">
                            {saveError && <p className="flex-1 text-[11px] font-semibold text-red-500">{saveError}</p>}
                            <button
                                onClick={onClose}
                                className="px-4 py-1.5 text-xs font-bold rounded-lg border border-[var(--border-color)] text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:border-[var(--text-muted)] transition-colors"
                            >
                                Fermer
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="px-4 py-1.5 text-xs font-bold rounded-lg bg-brand-600 hover:bg-brand-700 text-content transition-colors disabled:opacity-50"
                            >
                                {saving ? 'Enregistrement...' : saved ? '✓ Enregistré' : 'Enregistrer'}
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
}
